import { Toaster } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import SupportEmail from '../../components/supportEmail/SupportEmail';
import useIsMobile from '../../hooks/useIsMobile';

function TermsAndConditionsPage() {
  const { isMobile } = useIsMobile();

  return (
    <div
      className={`px-4 md:px-24 mx-auto py-10 leading-6 ${
        isMobile ? 'px-5' : ''
      }`}
    >
      <section>
        <Toaster />
        {!isMobile && (
          <h1 className='text-[1.25rem] leading-[2rem] md:text-[40px] text-[#2D2D2D] md:leading-[40px] mb-8 font-bold'>
            Terms and Conditions for using Savers App Online Services
          </h1>
        )}
        <p className='mb-6'>
          <strong>Effective Date: October 21, 2026</strong>
        </p>
        <div className='space-y-4'>
          <p>
            IMPORTANT: PLEASE READ THESE PROGRAM TERMS AND CONDITIONS OF USE
            CAREFULLY BEFORE USING THE PROGRAM, AS THEY AFFECT YOUR LEGAL RIGHTS
            AND OBLIGATIONS, INCLUDING, BUT NOT LIMITED TO, WAIVERS OF RIGHTS,
            LIMITATION OF LIABILITY, ARBITRATION OF DISPUTES AND YOUR INDEMNITY
            TO US. BY SELECTING THE ACCEPTANCE CONTROL IN THE ENROLLMENT FLOW,
            YOU AGREE TO THESE PROGRAM TERMS. IF YOU DO NOT AGREE TO ALL OF
            THESE PROGRAM TERMS, DO NOT ENROLL IN OR USE THE PROGRAM.
          </p>
          <p>
            The Savers App Program (the <strong>"Program"</strong>) means the
            Savers App rewards program in which you are enrolled, whether
            through the App or a Partner Channel. The Program is offered to our
            valued customers (<strong>"you"</strong> or <strong>"your"</strong>{' '}
            as the context dictates) by Savers App Inc. and its subsidiaries and
            affiliates (<strong>"Savers App"</strong>, <strong>"we"</strong>,{' '}
            <strong>"us"</strong> or <strong>"our"</strong> as the context
            dictates) under the terms and conditions (the{' '}
            <strong>"Program Terms"</strong>) stated below. Some features,
            including Round-Up, Donate Cashback and certain Payout Methods, may
            not be available in every Program.
          </p>
          <p>
            Program Terms govern your use of the Program and all related
            services and features such as widgets, mobile applications,
            websites, browser extensions, content, and downloads available
            through the App, regardless of how you access or use it. Savers App
            Inc., 1111B S Governors Ave, Unit 21598, Dover, DE 19904, T: +1
            (385) 518-0190 or its subsidiaries and/or affiliates operate this
            Program. The Program may be located at 'saversapp.com', may be
            hosted under any other domain Savers App Inc. or its partner(s)
            elect to operate the Program at, or may be offered to you within a
            Partner Channel.
          </p>
          <p>
            We use service providers (including the Program Vendor), payment
            networks, merchants and other partners to operate the Program. Those
            parties do not become parties to these Program Terms or receive a
            right to enforce them merely because they provide services. Any
            third-party terms that apply directly to you will be clearly
            identified and presented to you before the relevant service is used.
          </p>
          <p>
            Please review our{' '}
            <Link
              to={`${
                isMobile ? '/privacy-policy?ismobile=true' : '/privacy-policy'
              }`}
              className='font-semibold text-blue-700 hover:underline whitespace-nowrap'
              onClick={() => window.scrollTo(0, 0)}
            >
              Privacy Policy
            </Link>
            , which explains how Savers App collects, uses, discloses and
            retains personal information and how you may exercise privacy
            rights. The Privacy Policy is a notice and is not part of these
            Program Terms. You accept these Program Terms only when you take the
            affirmative action shown in the enrollment flow. We will request any
            consent required by law separately. We will not change settings or
            configurations on your device without your direction or permission.
          </p>
          <p>
            You are obligated to gain a complete understanding of the Program
            Terms and the functions and operation of the Program. In the event
            any FAQ comments, Program content, or any statements by our Customer
            Service Team conflict with these Program Terms, these Program Terms
            will govern.
          </p>
          <p>
            All capitalized terms not defined in the body of these Program Terms
            are defined below. By selecting the unchecked acceptance control in
            the enrollment flow, you agree to these Program Terms. If you do not
            agree, do not enroll in or use the Program.
          </p>
        </div>
      </section>

      <section className='px-2 md:px-0'>
        <ol className='list-decimal mt-8 bold-marker ml-[1.2rem]'>
          <li className='mb-5'>
            <h3 className='font-bold'>Definitions</h3>
            <div className='mt-2 space-y-3'>
              <p>
                <strong>"Eligible Card"</strong> means a credit or debit card on
                a supported Visa or Mastercard payment network that is eligible
                for enrollment in the applicable launch market. Some
                transactions, including PIN-debit or other transactions routed
                outside the supported network rails, cannot be detected and are
                not Qualifying Purchases. Card eligibility and technical
                limitations may vary by issuer, network, country and Offer and
                will be disclosed in the enrollment flow or applicable Offer.
              </p>
              <p>
                <strong>"Enrolled Card"</strong> or <strong>"Card"</strong>{' '}
                means an Eligible Card that has been registered for the Program
                by you under a Program Account.
              </p>
              <p>
                <strong>"Card Enrollment Consent"</strong> means the consent you
                give when enrolling an Eligible Card, as presented in the
                enrollment flow.
              </p>
              <p>
                <strong>"Payout Method"</strong> means a method offered in the
                App for receiving Confirmed Cashback, such as a Payout Card, that
                you have selected in your Program Account. The Payout Methods
                available to you are shown in the App.
              </p>
              <p>
                <strong>"Payout Card"</strong> means an Enrolled Card that you
                have designated in your Program Account to receive Confirmed
                Cashback. A Payout Card is a Payout Method but may not be
                available in your Program.
              </p>
              <p>
                <strong>"Card-linked Offer"</strong> means an offer to you from
                a participating Merchant that is made available via the Program
                and is redeemed in association with a Merchant by using your
                Enrolled Card.
              </p>
              <p>
                <strong>"Click Offer"</strong> means an offer to you from a
                participating Merchant that is made available via the Program
                and is redeemed in association with a Merchant by using the
                specified offer link and your Enrolled Card. You must click the
                offer link before completing your purchase for the purchase to
                qualify.
              </p>
              <p>
                <strong>"Code Offer"</strong> means an offer to you from a
                participating Merchant that is made available via the Program
                and is redeemed in association with a Merchant by using the
                specified code and your Enrolled Card.
              </p>
              <p>
                <strong>"Offer"</strong> means a Card-linked Offer, a Click
                Offer or a Code Offer made available to you in the Program by a
                Merchant.
              </p>
              <p>
                <strong>"Card-Use Data"</strong> means the transaction data
                provided by the corresponding Payment Network for an Enrolled
                Card and any data calculated by the Program related to that
                transaction data that is required to provide the benefits of the
                Program.
              </p>
              <p>
                <strong>"Pending Cashback"</strong> means a provisional reward
                recorded after a transaction is detected and before it becomes a
                Qualifying Purchase.
              </p>
              <p>
                <strong>"Confirmed Cashback"</strong> means Cashback earned on a
                Qualifying Purchase that Savers App has validated under the
                applicable Offer and made eligible for payout under these
                Program Terms.
              </p>
              <p>
                <strong>"Cashback Reward"</strong> or{' '}
                <strong>"Cashback"</strong> refers to Pending Cashback or
                Confirmed Cashback, as the context requires.
              </p>
              <p>
                <strong>"Closing Period"</strong> means the period that begins
                when your Program Account is canceled, terminated or deleted and
                during which your remaining Cashback Rewards are settled, as
                described in Section 13.
              </p>
              <p>
                <strong>"Charity"</strong> means the charitable organization
                designated in the Program to receive Donations, or any
                replacement organization designated under Section 7A.
              </p>
              <p>
                <strong>"Donate Cashback"</strong> means the optional Program
                feature described in Section 7A, under which all your Confirmed
                Cashback is donated to the Charity instead of being paid to you.
                No administrative or other fees are charged by Savers App for
                such donations.
              </p>
              <p>
                <strong>"Donation"</strong> means monthly Round-Up charges made
                to a Round-Up Card and, where Donate Cashback is on, Confirmed
                Cashback, in each case remitted to the Charity under Section 7A.
              </p>
              <p>
                <strong>"Round-Up"</strong> means the optional Program feature
                described in Section 7A, under which all purchases made with a
                Round-Up Card are rounded up to the next whole U.S. dollar, and
                the accumulated difference, subject to the Minimum Round-Up
                Donation and your Monthly Cap, is charged monthly and donated to
                the Charity, less the Round-Up Administrative Fee.
              </p>
              <p>
                <strong>"Round-Up Administrative Fee"</strong> means the fee
                retained by Savers App for handling and processing Round-Up
                donations, equal to 15% of each monthly Round-Up charge made to
                your Round-Up Card. It is deducted from the amount charged
                before the remainder is remitted to the Charity and is not
                charged to you in addition to the monthly Round-Up charge.
              </p>
              <p>
                <strong>"Round-Up Amount"</strong> means, for an individual
                purchase made with a Round-Up Card, the difference between the
                purchase amount and the next whole U.S. dollar. The Round-Up
                Amount for a purchase that is already a whole-dollar amount will
                be zero.
              </p>
              <p>
                <strong>"Round-Up Card"</strong> means an Enrolled Card for
                which you have enabled Round-Up and authorized the monthly
                Round-Up charge described in Section 7A.
              </p>
              <p>
                <strong>"Minimum Round-Up Donation"</strong> means the higher of
                $7 or the minimum Round-Up donation amount set by you in the App
                settings.
              </p>
              <p>
                <strong>"Monthly Cap"</strong> means the maximum amount that
                will be charged to each of your Round-Up Card(s) in a calendar
                month under Round-Up, as selected by you at setup and changeable
                in the App. The Monthly Cap cannot be set below your Minimum
                Round-Up Donation.
              </p>
              <p>
                <strong>"Merchant"</strong> means a third-party business that
                makes an Offer available to you through the Program.
              </p>
              <p>
                <strong>"Partner Channel"</strong> means an application,
                website, or other channel operated by or for a business partner
                of Savers App through which the Program is offered to that
                partner's customers.
              </p>
              <p>
                <strong>"Party"</strong> means either you or Savers App Inc.,
                and <strong>"Parties"</strong> means both you and Savers App
                Inc.
              </p>
              <p>
                <strong>"Payment Network"</strong> or{' '}
                <strong>"Payment Network Provider"</strong> means Visa,
                Mastercard, and other payment network companies that participate
                in the Program.
              </p>
              <p>
                <strong>"Program Vendor"</strong> means Olive Group Ltd, our
                technology vendor for the Program, which is a service provider to
                Savers App and not a party to these Program Terms.
              </p>
              <p>
                <strong>"Program Account"</strong> means the account that you
                set up to be entitled to receive Cashback Rewards, to participate
                in Round-Up and other donation features, and the other benefits
                of the Program.
              </p>
              <p>
                <strong>"Qualifying Purchase"</strong> means a purchase in
                connection with an Offer and made with an Enrolled Card that, in
                our sole discretion, satisfies all the requirements of the
                Offer. Qualifying Purchases made with an Enrolled Card while the
                card is not in Good Standing will not accrue any Cashback
                Rewards. An Enrolled Card will be in{' '}
                <strong>"Good Standing"</strong> if it (1) is in an active
                status and (2) is not lost or stolen. If you make a Qualifying
                Purchase in part with your Enrolled Card and in part with
                another form of payment, you must meet the terms of the Offer in
                the part of the purchase made with your Enrolled Card. You will
                only earn Cashback Rewards on the part of the purchase made with
                your Enrolled Card.
              </p>
              <p>
                <strong>"App"</strong> means the Savers App mobile application,
                website, and any subdomains, through which we make the Program
                available to you, regardless of how you access or use it.
              </p>
              <p>
                <strong>"Dispute"</strong> means any dispute, claim or
                controversy between you and Savers App arising out of or
                relating to the Program or these Program Terms.
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>Understanding our Role</h3>
            <div className='flex mt-2'>
              <strong>a. </strong>
              <p className='ml-2'>
                By participating in the Program you are choosing to use the
                Program to receive and accept certain Offers from Merchants that
                reward you for making certain transactions and, if you choose
                and where available in your Program, to make Donations to the
                Charity through Round-Up and Donate Cashback. Various parties'
                technology and services collectively allow us to provide you
                with these benefits. Nothing under the Program Terms in any way
                reduces your obligations and rights under separate terms and
                condition for your credit card or other services that you may be
                subscribed to with us or with third parties, including any terms
                between you and the operator of a Partner Channel. We and our
                licensors do not make any particular suggestion or
                recommendation to you in respect of how you avail yourself of the
                Program. See the <strong>"Additional Disclaimers"</strong>{' '}
                section below for further limitations.
              </p>
            </div>
            <div className='flex mt-2'>
              <strong>b. </strong>
              <p className='ml-2'>
                Where you access the Program through a Partner Channel, these
                Program Terms govern your relationship with Savers App in
                respect of the Program, and your relationship with the partner
                in respect of the partner's own products and services remains
                governed by your agreements with that partner. The partner is
                not a party to these Program Terms.
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>Right to Use &amp; Grants</h3>
            <div className='flex mt-2'>
              <strong>a. </strong>
              <p className='ml-2'>
                Subject to your compliance with the Program Terms, we grant you
                a non-sublicensable, non-transferable, non-exclusive, limited,
                and revocable right to access the Program.
              </p>
            </div>
            <div className='flex mt-2'>
              <strong>b. </strong>
              <p className='ml-2'>
                When you enroll an Eligible Card, you authorize the supported
                Payment Network and our service providers, including the Program
                Vendor, to use eligible transaction details to operate the
                card-linked Program as described in the Card Enrollment Consent
                and Privacy Policy. If you enable Round-Up on a Card, you also
                authorize the use of all transactions reported for that Card to
                calculate Round-Up Amounts, and the monthly charge described in
                Section 7A, under the separate authorization you give when you
                enable Round-Up. Marketing messages, precise-location access,
                cross-context behavioral advertising and any other activity that
                requires separate consent or opt-out will be controlled through
                the applicable choice mechanism.
              </p>
            </div>
            <div className='flex mt-2'>
              <strong>c. </strong>
              <p className='ml-2'>
                You grant us and our licensors the right to use Card
                transactions and Card-Use Data for the purposes of improving
                services and the Program. We do not sell your information,
                including your Card transactions and Card-Use Data.
              </p>
            </div>
            <div className='flex mt-2'>
              <strong>d. </strong>
              <p className='ml-2'>
                Savers App and its licensors reserve all intellectual property
                rights in all elements (including but not limited to design, all
                text, graphics, content, video, logos, audio and the selection
                and arrangement of the foregoing) of the App associated with the
                Program. You agree not to reproduce, duplicate, copy, sell,
                resell or exploit any content or feature on the App.
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>
              Program Participation Conditions.{' '}
              <span className='font-normal'>
                The following conditions apply to you during your participation
                in the Program:
              </span>
            </h3>
            <div className='flex mt-2 ml-4'>
              <ol className='list-decimal'>
                <li>
                  you must register for a Program Account on our App or through
                  a Partner Channel and inform us promptly of any changes to the
                  information provided in the creation of your Program Account;
                  a general membership is free. You are only permitted to
                  register once and must provide accurate name, email and other
                  mandatorily requested information, which may change from time
                  to time. All submitted information must be true.
                </li>
                <li>
                  you must use reasonable care to protect your Program Account
                  credentials and promptly notify us if you believe they have
                  been compromised. You are responsible for activity you
                  authorize or cause. You are not responsible for unauthorized
                  activity caused by our failure to use reasonable security, and
                  nothing in these Program Terms limits rights or protections
                  that cannot lawfully be waived. We may require a credential
                  reset or temporarily restrict access to protect the Program
                  Account.
                </li>
                <li>
                  if any communication you receive looks suspicious or has
                  hyperlinks that you do not expect, do not open the
                  communication or click the hyperlink. Instead, email us at{' '}
                  <SupportEmail />
                </li>
                <li>
                  only the Program Account holder can enroll their Eligible Card
                  in the Program;
                </li>
                <li>
                  if your Enrolled Card is canceled or expires, you must update
                  the Program Account with current information in order to be
                  eligible to receive benefits under the Program; if a Round-Up
                  Card is replaced, Round-Up must be enabled and authorized
                  again on the replacement Card;
                </li>
                <li>
                  you may cease participation in the Program at any time by
                  deleting your Program Account, which will release you from
                  these Program Terms, subject to Section 13. To do so, unenroll
                  all your Enrolled Cards and then delete your Program Account,
                  both of which can be done in the App. Unenrolling a Round-Up
                  Card ends Round-Up on that Card; see Section 13 for any
                  accumulated Round-Up Amount at that time; and
                </li>
                <li>
                  to use the Program, you must be an individual who has reached
                  the age of majority in the jurisdiction where you reside and,
                  in all events, be at least 18 years old. The Program is not
                  offered to legal entities under these member terms.
                </li>
              </ol>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>
              Savers App One-Time Password (OTP) Terms of Service
            </h3>
            <div className='mt-2 space-y-3'>
              <p>
                The Program is a mobile-first Offers program that is owned and
                operated by Savers App. As a member, you can enjoy Card-linked
                and online Offers across a wide variety of Merchants, receive
                tailored regional recommendations, and gain access to online and
                offline retailers across various shopping categories in the
                markets where the Program operates. You earn Cashback Rewards
                with participating Merchants when you use the Visa or Mastercard
                credit or debit cards you have enrolled in the Program through
                the App or a Partner Channel. If your Program includes Round-Up
                and Donate Cashback, you may also choose to donate to the
                Charity through these features. We may send verification
                messages by SMS to your phone number. We will send you
                additional SMS messages only if you opt in to them in the
                Notifications section in the App. Donation milestone and
                Round-Up charge messages sent by SMS follow the same
                notification preferences.
              </p>
              <p>
                If you have opted in for other types of SMS communication, you
                can opt out of such SMS service at any time by modifying your
                Notification preferences in the App.
              </p>
              <p>
                If you are experiencing issues with the messaging program you
                can reply to the SMS message with the keyword HELP for more
                assistance, or you can get help directly at <SupportEmail />.
              </p>
              <p>
                Carriers are not liable for delayed or undelivered messages.
              </p>
              <p>
                As always, message and data rates may apply for any messages
                sent to you from us and to us from you. You may receive One-Time
                Password messages when you log in. Other types of messages will
                be sent based on the preferences you select in the Notifications
                section in the App. If you have any questions about your text
                plan or data plan, it is best to contact your wireless provider.
              </p>
              <p>
                If you have any questions regarding privacy, please read our{' '}
                <Link
                  to={`${
                    isMobile
                      ? '/privacy-policy?ismobile=true'
                      : '/privacy-policy'
                  }`}
                  className='font-semibold text-blue-700 hover:underline whitespace-nowrap'
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Privacy Policy
                </Link>{' '}
                at https://members.saversapp.com/privacy-policy.
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>
              Expulsion of Users, Inactivity &amp; Change of Offers
            </h3>
            <div className='mt-2 space-y-3'>
              <p>
                Savers App has the discretion to immediately expel a user,
                suspend or terminate a user's account or licenses in its
                business judgment, including, without limitation, if the user
                has violated legal regulations, third-party rights, or these
                Program Terms; or has violated, endangered, or infringed upon a
                legitimate interest of Savers App, particularly in case of
                outstanding payments or other matters for the protection of
                other users.
              </p>
              <p>
                If no purchase in connection with an Offer is made or Round-Up
                charge occurs on an Enrolled Card for 90 days, we may, after at
                least thirty (30) days' direct notice, unenroll the inactive
                Card for security or operational reasons. Inactivity alone will
                not forfeit Confirmed Cashback. You may re-enroll an Eligible
                Card at any time, subject to then-current enrollment
                requirements.
              </p>
              <p>
                We may change or discontinue Program features or future Offers.
                If a change is material, we will give you reasonable advance
                direct notice unless immediate action is reasonably required for
                security, fraud prevention, law or network requirements. A
                change will apply prospectively and will not reduce Confirmed
                Cashback or retroactively change the terms of a purchase already
                made. If you do not agree to a material change to these Program
                Terms, you may delete your Program Account before the change
                takes effect.
              </p>
              <p>
                You may also delete your Program Account yourself at any time
                from your account settings, as described in Section 4(6).
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>Program Mechanics</h3>
            <div className='mt-2 space-y-3'>
              <p>
                Program participation automatically entitles each transaction
                completed with the Enrolled Card to be considered for
                eligibility for all Offers available under the Program. This
                section explains the different types of Offers available to you,
                and how the Cashback Rewards you earn under those Offers are
                calculated, credited, and paid out.
              </p>
              <p>
                <strong>Offers.</strong> The Program currently provides three
                types of Offers, described below.
              </p>
              <p>
                <strong>Card-linked Offers:</strong> this type of offer is
                applied automatically when you use your Enrolled Card for an
                eligible transaction with a participating Merchant. No further
                action is required from you.
              </p>
              <p>
                <strong>Click Offers:</strong> this type of offer requires you
                to click the offer link in the App before completing your
                purchase using your Enrolled Card, in order for the transaction
                to be tracked and qualify. Terms of Click Offers are indicated
                in the text of the Offer and are subject to Merchant discretion.
              </p>
              <p>
                <strong>Code Offers:</strong> this type of offer requires you to
                enter the specified code at checkout, in addition to using your
                Enrolled Card, in order for the purchase to qualify. Terms of
                Code Offers are indicated in the text of the Offer and are
                subject to Merchant discretion.
              </p>
              <p>
                <strong>Cashback Rewards.</strong> Cashback Rewards are earned
                when a transaction qualifies for an Offer, regardless of which
                Offer type it was redeemed under. The following explains how
                Cashback Rewards are calculated, credited, and paid out.
              </p>
              <p>
                When you use your Enrolled Card in a transaction that qualifies
                for an Offer (as determined by the Payment Network), the
                Cashback Rewards you earn will be credited to your Program
                Account by the Program. Only Eligible Cards are eligible to be
                enrolled in and benefit from the program. The list of eligible
                cards may change from time to time without any liability to you.
              </p>
              <p>
                <strong>Nature of Cashback.</strong> Pending Cashback is
                promotional and contingent on final validation under the Offer.
                Confirmed Cashback is eligible for payout under these Program
                Terms, subject only to the disclosed payout threshold, method
                and timing and lawful reversals described below. The Program
                Account is a recordkeeping account, not a deposit account,
                wallet or payment instrument. Donation funds are received by
                Savers App and held only until they are remitted to the Charity,
                as described in Section 7A. Nothing in this description
                overrides rights under applicable law.
              </p>
              <p>
                We will generally confirm eligible Cashback within 120 days
                after the purchase, although Merchant, network or return periods
                may cause a longer disclosed period. We will pay Confirmed
                Cashback to your Payout Method as confirmed in the App each
                month when the disclosed minimum payout threshold of $10 is met.
                If your balance is below the threshold, it will carry forward.
                If your Program Account is canceled, terminated or deleted, your
                remaining Cashback Rewards are settled as described in Section
                13. If Donate Cashback is on, Confirmed Cashback is donated to
                the Charity instead of being paid to you, as described in
                Section 7A. You may contact support to dispute missing or
                incorrect Cashback Rewards, and we will investigate in good
                faith. Any deadline that applies to a claim will be clearly
                disclosed and reasonable.
              </p>
              <p>
                <strong>Partner Channels.</strong> Where you participate in the
                Program through a Partner Channel, your Confirmed Cashback is
                credited to your account as determined by and agreed with the
                operator of that Partner Channel. The crediting schedule will be
                disclosed in the Partner Channel. All other provisions of these
                Program Terms, including validation, reversals and forfeiture,
                apply equally in Partner Channels.
              </p>
              <p>
                Unless otherwise specified in the Program Terms or in an Offer,
                the amount of Cashback Rewards will be calculated based on the
                entire amount spent on an Enrolled Card in a transaction. The
                Merchant may stipulate a maximum eligible Cashback Reward amount
                or include or eliminate gratuity amounts from the total amount
                spent on a given transaction by an Enrolled Card for the
                purposes of calculating Cashback Rewards. Please read the
                details of any Offer that you wish to avail yourself of in
                respect of your Enrolled Card transaction to ensure you fully
                understand the Offer.
              </p>

              <h3 className='font-bold pt-4'>7A. Round-Up and Donations</h3>
              <div className='flex mt-2'>
                <strong>a. </strong>
                <p className='ml-2'>
                  <strong>Optional features.</strong> Where available in your
                  Program, Round-Up and Donate Cashback are optional and off by
                  default. If you do not turn them on, your Cashback is paid to
                  you under Section 7 and no Donation is made.
                </p>
              </div>
              <div className='flex mt-2'>
                <strong>b. </strong>
                <p className='ml-2'>
                  <strong>Enabling Round-Up.</strong> You may enable Round-Up
                  separately for each Enrolled Card, either when you enroll the
                  Eligible Card or later from the Cards section in the App, by
                  selecting your Monthly Cap and authorizing the monthly
                  Round-Up charge in the App. Round-Up is not active on a Card
                  until you give that authorization.
                </p>
              </div>
              <div className='flex mt-2'>
                <strong>c. </strong>
                <p className='ml-2'>
                  <strong>Calculation.</strong> For each purchase made with a
                  Round-Up Card that is reported to the Program by the Payment
                  Network, we calculate a Round-Up Amount, whether or not the
                  purchase is a Qualifying Purchase. Transactions that cannot be
                  detected, including PIN-debit or other transactions routed
                  outside the supported network rails, do not generate a
                  Round-Up Amount.
                </p>
              </div>
              <div className='flex mt-2'>
                <strong>d. </strong>
                <p className='ml-2'>
                  <strong>Monthly Cap.</strong> We will not charge more than
                  your Monthly Cap in any calendar month. You may change your
                  Monthly Cap in the App; a change applies from the next charge.
                </p>
              </div>
              <div className='flex mt-2'>
                <strong>e. </strong>
                <p className='ml-2'>
                  <strong>Monthly charge.</strong> Once each month, on or about
                  the 3rd of each month, Savers App will charge your Round-Up
                  Card for the higher of either your accumulated Round-Up Amount
                  or Minimum Round-Up Donation, up to your Monthly Cap, under
                  the authorization you gave. The charge will appear on your
                  Card statement as SAVERSAPP* RU DONATION. If a charge is
                  declined or fails, it will be retried over a period of up to
                  14 days, and we will notify you so you can update your
                  Round-Up Card or, if you choose, pay through a payment link we
                  send you. If the charge has not succeeded after 14 days, it
                  will be canceled and not charged. If the charge also fails the
                  following month after the same 14-day retry period, that
                  charge will be canceled, Round-Up will be turned off on that
                  Card and we will notify you. The Card remains an Enrolled Card
                  for Cashback Rewards subject to Section 6. You can turn
                  Round-Up on again from the Cards section in the App, which
                  requires a new authorization of the monthly Round-Up charge.
                </p>
              </div>
              <div className='flex mt-2'>
                <strong>f. </strong>
                <p className='ml-2'>
                  <strong>Donate Cashback.</strong> You may turn on Donate
                  Cashback in the App. While it is on, all of your Confirmed
                  Cashback is donated to the Charity instead of being paid to
                  you; partial allocation is not available. Pending Cashback is
                  donated only when it becomes Confirmed. If you turn Donate
                  Cashback off, Cashback that becomes Confirmed after that time
                  is paid to you under Section 7.
                </p>
              </div>
              <div className='flex mt-2'>
                <strong>g. </strong>
                <p className='ml-2'>
                  <strong>Handling and remittance of Donation funds.</strong>{' '}
                  Savers App is not a charity. Monthly Round-Up charges are
                  collected by Savers App. Savers App remits Donations to the
                  Charity within 30 days of receipt, less the Round-Up
                  Administrative Fee on Round-Up charges. No fee is deducted
                  from donated Cashback. Apart from the Round-Up Administrative
                  Fee, Donation funds are held only for the purpose of remitting
                  them to the Charity and are not used for Savers App's own
                  purposes.
                </p>
              </div>
              <div className='flex mt-2'>
                <strong>h. </strong>
                <p className='ml-2'>
                  <strong>Receipts, statements and tax treatment.</strong> You
                  can view your monthly Donation totals and download monthly
                  statements in the App. Donation tax receipts, if applicable,
                  would be issued by the Charity directly. To enable this, in
                  Programs that include Round-Up or Donate Cashback we share
                  with the Charity your name, email address and the dates and
                  amounts of your Donations, as described in the Privacy Policy.
                  The Charity uses that information under its own privacy
                  policy.
                </p>
              </div>
              <div className='flex mt-2'>
                <strong>i. </strong>
                <p className='ml-2'>
                  <strong>Turning Round-Up off.</strong> You may turn off
                  Round-Up on any Card at any time in the App. Turning it off
                  takes effect immediately and any accumulated Round-Up Amount
                  not yet charged will be canceled and not charged.
                </p>
              </div>
              <div className='flex mt-2'>
                <strong>j. </strong>
                <p className='ml-2'>
                  <strong>Returns, refunds and errors.</strong> Donations are
                  generally non-refundable once remitted to the Charity. If a
                  purchase made with a Round-Up Card is returned or refunded
                  after the Round-Up Amount has been charged to your Round-Up
                  Card, its Round-Up Amount is not reversed. If a Round-Up
                  charge is made in error, including above your Monthly Cap or
                  after you turned Round-Up off, contact <SupportEmail /> and we
                  will refund the erroneous amount within the standard
                  processing time of 7-10 business days. Nothing in this section
                  limits any right you have to dispute a charge with your Card
                  issuer.
                </p>
              </div>
              <div className='flex mt-2'>
                <strong>k. </strong>
                <p className='ml-2'>
                  <strong>Changes to Round-Up and the Charity.</strong> We may
                  change or discontinue Round-Up or Donate Cashback, or
                  designate a different Charity, prospectively under clause 10
                  (Changes to These Terms) of Section 14. We will give you
                  direct advance notice before any change to the Round-Up
                  calculation, the charge schedule or the Charity, and we will
                  obtain a new authorization where required.
                </p>
              </div>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>Forfeiture or Reversal of Rewards</h3>
            <div className='mt-2 space-y-3'>
              <p>
                We may reverse Pending or Confirmed Cashback only to correct a
                return, cancellation, chargeback, duplicate credit, manifest
                calculation error, ineligible transaction, fraud or material
                abuse supported by reasonable evidence. We will not reverse
                Cashback based solely on an undisclosed rule or after the
                applicable validation/return period except for fraud, a network
                correction or legal requirement. When practicable, we will
                explain the reason and provide a reasonable review process. We
                may offset a valid reversal against future Cashback, but we will
                not debit your Card or bank account without separate
                authorization. The monthly Round-Up charge is made only under
                the separate authorization described in Section 7A. If Cashback
                already donated to the Charity is later reversed, it will be
                offset against future Cashback. Closing a Payout Card does not
                by itself forfeit Confirmed Cashback; you must select another
                Payout Method.
              </p>
              <p>
                <strong>Improper Activity.</strong> The Program is limited to
                one Program Account per person. "Improper Activity" means: (a)
                knowingly creating or using more than one Program Account, or
                closing and re-creating Program Accounts to circumvent Program
                rules, including payout thresholds; (b) abuse of any referral or
                invitation program, including self-referrals, fictitious
                accounts, or referrals procured through misrepresentation; (c)
                synthetic, manipulated, or self-dealing transactions designed to
                generate Cashback; or (d) any other material breach of these
                Program Terms committed to obtain Cashback Rewards. Where
                Improper Activity is supported by reasonable evidence, we may
                reverse or forfeit only the portion of Pending or Confirmed
                Cashback reasonably attributable to the Improper Activity;
                provided that, for a duplicate Program Account knowingly created
                or used, we may reverse or forfeit Cashback Rewards credited to
                that duplicate account. We may also suspend or terminate the
                Program Account involved. When practicable and lawful, we will
                give you notice of the reason and a reasonable opportunity to
                seek review through support.
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>Indemnification</h3>
            <p className='mt-2'>
              To the extent permitted by applicable law, you are responsible for
              direct losses we reasonably incur because of your intentional
              fraud, willful misconduct or material unlawful use of the Program.
              You are not required to indemnify us for our own acts or
              omissions, ordinary consumer use, or claims that applicable law
              does not permit us to shift to you.
            </p>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>Limitation of Liability</h3>
            <div className='mt-2 space-y-3'>
              <p>
                IN NO EVENT WILL SAVERS APP AND ITS LICENSORS, SERVICE
                PROVIDERS, VISA OR MASTERCARD, BE LIABLE TO YOU FOR ANY
                INDIRECT, PUNITIVE, SPECIAL, INCIDENTAL, EXEMPLARY OR
                CONSEQUENTIAL DAMAGES, DAMAGES FOR LOSS OF USE, DATA,
                INFORMATION, OR REVENUE, PROFITS OR BUSINESS INTERRUPTION,
                WHATSOEVER IN RELATION TO OR ARISING UNDER THE PROGRAM TERMS,
                WHETHER SUCH DAMAGES ARE BASED IN TORT, CONTRACT, NEGLIGENCE,
                STRICT LIABILITY OR OTHERWISE, EVEN IF WE HAVE BEEN ADVISED OF
                THE POSSIBILITY OF SUCH DAMAGES.
              </p>
              <p>
                TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, SAVERS APP'S
                AGGREGATE LIABILITY FOR DIRECT DAMAGES ARISING FROM THE PROGRAM
                WILL NOT EXCEED THE GREATER OF (A) USD $100 OR (B) THE CONFIRMED
                CASHBACK OWED TO YOU. THIS LIMIT DOES NOT APPLY TO FRAUD OR
                WILLFUL MISCONDUCT, A SECURITY OR PRIVACY BREACH TO THE EXTENT
                LIABILITY CANNOT LAWFULLY BE LIMITED, ANY ROUND-UP AMOUNT
                CHARGED IN ERROR, OR ANY WARRANTY, REMEDY OR CONSUMER RIGHT THAT
                APPLICABLE LAW DOES NOT PERMIT US TO DISCLAIM OR LIMIT.
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>Additional Disclaimers</h3>
            <div className='mt-2 space-y-3'>
              <p>
                You waive and release Savers App, its affiliates, licensors and
                their subsidiaries, affiliates, partners, officers, directors,
                employees and agents from any liabilities arising from or
                related to any act or omission of a Merchant in connection with
                your use or redemption of the Program or the goods, services or
                experiences that a Merchant provides in connection with the
                Program. Savers App does not control, and is not responsible
                for, the Charity's activities, its use of Donations after they
                are remitted, or its use of the information shared with it under
                Section 7A(h).
              </p>
              <p>
                THE SERVICES ARE PROVIDED FOR YOUR CONVENIENCE, "AS IS" AND "AS
                AVAILABLE", AND SAVERS APP AND ITS AFFILIATES, LICENSORS AND
                SUPPLIERS, INCLUDING OUR SERVICE PROVIDERS, VISA AND MASTERCARD,
                EXPRESSLY DISCLAIM ANY WARRANTIES AND CONDITIONS OF ANY KIND,
                WHETHER EXPRESSED OR IMPLIED, INCLUDING THE WARRANTIES OR
                CONDITIONS OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                ACCURACY AND NON-INFRINGEMENT.
              </p>
              <p>
                WE DO NOT WARRANT ANY THIRD-PARTY, ITS PRODUCTS, OR SERVICES,
                INCLUDING THIRD-PARTY LINKS. WE DO NOT VERIFY THE ACCURACY OR
                RELIABILITY OF ANY INFORMATION ON ANY THIRD-PARTY PROPERTY,
                INCLUDING MATERIALS AND PERSON'S COMMENTS ON ANY SITE, AND DO
                NOT REVIEW, APPROVE, MONITOR, ENDORSE, WARRANT, OR MAKE ANY
                REPRESENTATIONS OF ANY KIND IN RESPECT OF SUCH MATERIALS.
              </p>
              <p>
                YOUR ONLY REMEDY WITH RESPECT TO ANY DISSATISFACTION WITH THE
                PROGRAM, OTHER SERVICES OR ANY MATERIALS, WILL BE TO CANCEL YOUR
                PROGRAM ACCOUNT AND TO STOP USING THE PROGRAM.
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>Term</h3>
            <p className='mt-2'>
              These Program Terms and the license granted hereunder shall remain
              in effect until (a) you have unenrolled all your Cards from the
              Program and deleted your Program Account, as described in Section
              4, or (b) we terminate your Program Account under Section 6 or
              Section 13.
            </p>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>
              Termination and Suspension of Program
            </h3>
            <div className='mt-2 space-y-3'>
              <p>
                Terms for Offers may change, or the Offer may expire or
                terminate at any time without additional obligation to you.
                Neither Savers App nor its license source or third-party service
                provider involved in the Program shall be liable to you for any
                suspension, modification or termination of an Offer or the
                Program.
              </p>
              <p>
                Without limiting anything else in the Program Terms, Savers App
                reserves the right, in its sole discretion, to suspend or
                terminate your participation in one or more Offers if we suspect
                that any information you provide is inaccurate or incomplete, or
                you fail to meet the eligibility requirements, fail to comply
                with the terms of any Offers or otherwise violate the Program
                Terms.
              </p>
              <p>
                <strong>Closing Period.</strong> Upon cancellation, termination
                or deletion, your license to use the Program ends and your
                Program Account enters the Closing Period. The Closing Period
                runs for ninety (90) days from the date of closure. If Pending
                Cashback recorded before closure is still awaiting validation at
                the end of those ninety (90) days, the Closing Period continues
                until that validation is complete, but never beyond one hundred
                and eighty (180) days from the date of closure. During the
                Closing Period no new Cashback Rewards are earned, Pending
                Cashback recorded before closure continues through validation
                and becomes Confirmed Cashback if validated, and lawful
                reversals under Section 8 may be applied to your balance.
              </p>
              <p>
                <strong>Final settlement.</strong> Unless Donate Cashback is on
                at closure (see below), at the end of the Closing Period we will
                pay all Confirmed Cashback in your Program Account, including
                Pending Cashback that became Confirmed Cashback during the
                Closing Period, to your Payout Method in a single payment, net
                of lawful reversals. The minimum payout threshold does not apply
                to this payment and no fee is deducted from it. Pending Cashback
                that has not been validated by the end of the Closing Period
                does not become Confirmed Cashback and is canceled. If we cannot
                complete the payment because you have not selected a Payout
                Method or because payment to your Payout Method fails, we will
                notify you using the contact information on file and continue to
                hold the amount, subject to applicable unclaimed-property law,
                until it can be paid to a Payout Method you select. We may
                suspend the final settlement while investigating suspected fraud
                but will complete the review within a reasonable time and
                communicate the outcome.
              </p>
              <p>
                <strong>Partner Channels.</strong> Where you participate through
                a Partner Channel, any Confirmed Cashback at the time of closure
                is instead credited to your account with the Partner Channel
                operator, subject to lawful reversals, and the minimum payout
                threshold and the Closing Period hold do not apply. Pending
                Cashback recorded before closure is credited to that account if
                and when it becomes Confirmed Cashback.
              </p>
              <p>
                <strong>Round-Up and Donate Cashback at closure.</strong> On
                cancellation, termination or deletion, your authorization for
                the monthly Round-Up charge ends and no further Round-Up charge
                will be made. Any accumulated Round-Up Amount will be canceled
                and not charged. If Donate Cashback is on at closure, Confirmed
                Cashback, including Pending Cashback that becomes Confirmed
                Cashback after closure, is donated to the Charity instead of
                being paid or credited to you: at the end of the Closing Period,
                net of lawful reversals, or, where you participate through a
                Partner Channel, when it becomes Confirmed Cashback.
              </p>
              <p>
                What happens to your personal information after your Program
                Account is deleted is described in our Privacy Policy. When you
                delete your Program Account, we delete the personal information
                in it that applicable law and our retention schedule do not
                require us to keep. During the Closing Period we keep only what
                the final settlement needs: an identifier for your Program
                Account (including, in a Partner Channel, the identifier
                assigned to you by the Partner Channel operator), your Payout
                Method, one contact channel for settlement notices, and your
                Cashback Reward records. Records we must keep after the Closing
                Period under applicable law or our retention schedule are
                described in the Privacy Policy. Notwithstanding anything to the
                contrary in the Program Terms, with respect to information and
                materials in our possession or control at the time of
                cancellation, termination or deletion of your Program Account:
                (i) we may retain your data in our backup, archive and disaster
                recovery systems until such data is deleted in the ordinary
                course; and (ii) all information and materials described in the
                foregoing clause shall remain subject to all confidentiality,
                security and other applicable requirements of the Program Terms.
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>Miscellaneous</h3>
            <div className='mt-2'>
              <ol className='list-decimal ml-4'>
                <li className='mt-4'>
                  <strong>Governing Law. </strong>
                  These Program Terms are governed by Delaware law and
                  applicable U.S. federal law, without regard to conflict-of-law
                  rules, except that this choice does not deprive you of
                  mandatory consumer, privacy or other protections of the
                  jurisdiction where you reside.
                </li>
                <li className='mt-4'>
                  <strong>Dispute Resolution through Arbitration. </strong>
                  If you reside in the United States, and unless applicable law
                  prohibits arbitration, a Dispute will be resolved by
                  individual binding arbitration administered by the American
                  Arbitration Association under its Consumer Arbitration Rules
                  in effect when the claim is filed, subject to the opt-out and
                  small-claims rights below. Savers App will pay fees to the
                  extent required by those Rules and applicable law. At your
                  election, a hearing will occur remotely or in the county where
                  you reside. Either party may bring an individual claim in an
                  appropriate small-claims court. This clause does not require
                  arbitration of a claim for public injunctive relief to the
                  extent applicable law makes that waiver unenforceable. If you
                  reside in Quebec, or another jurisdiction where a pre-dispute
                  consumer arbitration requirement is prohibited, this clause
                  does not apply to you.
                </li>
                <li className='mt-4'>
                  <strong>Arbitration Opt-Out. </strong>
                  You may opt out of the arbitration requirement in clause 2 by
                  sending written notice of your decision to opt out to the
                  notice address in clause 7 (Notices), or by email to{' '}
                  <SupportEmail />, within thirty (30) days of the date you
                  first accept these Program Terms. Your notice must include
                  your name and the email address associated with your Program
                  Account. Opting out of arbitration does not affect any other
                  provision of these Program Terms.
                </li>
                <li className='mt-4'>
                  <strong>Interim Relief. </strong>
                  Prior to the appointment of the arbitrator, the Parties may
                  apply to the courts for interim relief. A request for interim
                  relief by a Party to court will not be considered to be
                  incompatible with clause 2 (Dispute Resolution through
                  Arbitration) of this Section 14 or as a waiver of that
                  provision.
                </li>
                <li className='mt-4'>
                  <strong>Individual Proceedings. </strong>
                  To the fullest extent permitted by applicable law, each Party
                  may bring claims only in an individual capacity and not as a
                  plaintiff or class member in a purported class or
                  representative proceeding. The arbitrator may award relief
                  only to the individual claimant and only to the extent
                  necessary to resolve that claimant's claim. This paragraph
                  does not restrict a non-waivable right to public injunctive,
                  class or representative relief. If a court finds a particular
                  claim or remedy cannot lawfully be arbitrated or waived, that
                  claim or remedy will be severed and may proceed in court after
                  the arbitrable issues are resolved, unless applicable law
                  requires otherwise.
                </li>
                <li className='mt-4'>
                  <strong>Force Majeure. </strong>
                  We will not be responsible or liable to you, or deemed in
                  default or breach hereunder by reason of any failure or delay
                  in the performance of our obligations hereunder where such
                  failure or delay is due to strikes, labor disputes, civil
                  disturbances, riot, rebellion, invasion, hostilities, war,
                  terrorist attack, embargo, natural disaster, epidemics,
                  pandemics, including, but not limited to, the 2019 novel
                  coronavirus disease (COVID-19) pandemic, acts of God, flood,
                  tsunami, fire, sabotage, fluctuations or non-availability of
                  electrical power, heat, light, air conditioning or your
                  equipment, loss and destruction of property or any other
                  circumstances or causes beyond our reasonable control.
                </li>
                <li className='mt-4'>
                  <strong>Notices. </strong>
                  All notices, requests, consents, claims, demands, waivers and
                  other communications hereunder shall be in writing and shall
                  be deemed to have been given: (i) when delivered by hand; (ii)
                  when received by the addressee if sent by a nationally
                  recognized overnight courier (receipt requested); (iii) on the
                  date sent by facsimile or e-mail (with confirmation of
                  transmission) if sent during normal business hours of the
                  recipient, and on the next business day if sent after normal
                  business hours of the recipient; or (iv) on the second day
                  after the date mailed, by certified or registered mail by
                  USPS, return receipt requested, postage prepaid. Communications
                  to us are to be provided to Savers App Inc., 1111B S Governors
                  Ave, Unit 21598, Dover, DE 19904, Attention: Legal. We will
                  communicate notices to you via the information you provide us
                  with for the purposes of providing notice.
                </li>
                <li className='mt-4'>
                  <strong>Entire Agreement. </strong>
                  These Program Terms, together with all other documents that
                  are incorporated by reference herein, constitute the sole and
                  entire agreement between the Parties with respect to the
                  subject matter contained herein, and supersede all prior and
                  contemporaneous understandings, agreements, representations
                  and warranties, both written and oral, with respect to such
                  subject matter.
                </li>
                <li className='mt-4'>
                  <strong>
                    Successors and Assigns; No Third-Party Beneficiaries.{' '}
                  </strong>
                  These Program Terms benefit only you and Savers App and their
                  permitted successors and assigns. No Merchant, Payment
                  Network, service provider or other person is a third-party
                  beneficiary unless these Program Terms expressly identify that
                  person by name and state the specific provision it may
                  enforce. The Charity is not a third-party beneficiary of these
                  Program Terms.
                </li>
                <li className='mt-4'>
                  <strong>Changes to These Terms. </strong>
                  We may change these Program Terms prospectively. We will post
                  the updated Program Terms with a new effective date and
                  provide at least thirty (30) days' direct advance notice of a
                  material change, unless shorter notice is reasonably required
                  by law, security, fraud prevention, or card-network
                  requirements. Updated Program Terms apply from the date you
                  accept them if you enroll after they are posted, and from the
                  effective date stated in the notice if you are an existing
                  member. A material change to the reward-state definitions or
                  the point at which Cashback becomes owed will apply only to
                  purchases made on or after the effective date; Cashback
                  attributable to earlier Qualifying Purchases will remain
                  governed by the terms in effect when those transactions
                  occurred. A change to the Round-Up calculation, the Round-Up
                  charge schedule or the Charity is a material change; it will
                  apply only to purchases made on or after its effective date,
                  and we will obtain a new authorization for the monthly
                  Round-Up charge before it applies where required by law or
                  network rules. If applicable law or the nature of the change
                  requires renewed acceptance or consent, we will obtain it
                  before the change applies. No change will retroactively reduce
                  Confirmed Cashback. Your continued use after the effective
                  date may constitute acceptance only where applicable law
                  permits, and the notice clearly explains that consequence.
                </li>
                <li className='mt-4'>
                  <strong>Waiver. </strong>
                  No waiver by any Party of any of the provisions hereof shall
                  be effective unless explicitly set forth in writing and signed
                  by the Party so waiving. Except as otherwise set forth in
                  these Program Terms, no failure to exercise, or delay in
                  exercising, any right, remedy, power or privilege arising from
                  these Program Terms shall operate or be construed as a waiver
                  thereof; nor shall any single or partial exercise of any
                  right, remedy, power or privilege hereunder preclude any other
                  or further exercise thereof or the exercise of any other
                  right, remedy, power or privilege.
                </li>
                <li className='mt-4'>
                  <strong>Severability. </strong>
                  If any term or provision of these Program Terms is invalid,
                  illegal, or unenforceable in any jurisdiction, such
                  invalidity, illegality or unenforceability shall not affect
                  any other term or provision of these Program Terms or
                  invalidate or render unenforceable such term or provision in
                  any other jurisdiction.
                </li>
                <li className='mt-4'>
                  <strong>Language. </strong>
                  If you contract in Quebec, a French version of these Program
                  Terms and related documents will be presented to you first.
                  After reviewing the French version, you may expressly choose
                  to be bound by the English version where Quebec law permits. /
                  Langue. Si vous contractez au Quebec, une version francaise
                  des presentes modalites et des documents connexes vous sera
                  d'abord presentee. Apres avoir examine la version francaise,
                  vous pourrez choisir expressement d'etre lie par la version
                  anglaise lorsque la loi quebecoise le permet.
                </li>
                <li className='mt-4'>
                  <strong>Headings. </strong>
                  The headings in these Program Terms are for reference only and
                  do not affect the interpretation of these Program Terms.
                </li>
              </ol>
            </div>
          </li>
        </ol>
      </section>
    </div>
  );
}

export default TermsAndConditionsPage;
